// Static weights, not the variable fonts: some privacy browsers (Helium)
// ignore the weight axis of a variable font and draw every weight alike.
import "@fontsource/nunito/500.css";
import "@fontsource/nunito/600.css";
import "@fontsource/nunito/700.css";
import "@fontsource/nunito/800.css";
import "@fontsource/fredoka/500.css";
import "@fontsource/fredoka/600.css";
import "@fontsource/fredoka/700.css";
import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { routeTree } from "./routeTree.gen";
import { authClient } from "./lib/auth-client";

// Keep local OAuth on one site. Browsers treat localhost and 127.0.0.1 as
// different cookie hosts, so starting on localhost can lose Better Auth's
// state cookie before Google redirects back to the API.
if (window.location.hostname === "localhost") {
  const canonicalUrl = new URL(window.location.href);
  canonicalUrl.hostname = "127.0.0.1";
  window.location.replace(canonicalUrl.href);
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  context: { queryClient, authClient },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root")!;

function render() {
  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </React.StrictMode>
  );
}

// /sign-in arrives prerendered (scripts/prerender-sign-in.mjs). Load the
// route's chunk before the first render, so React swaps the static page for
// the live one in a single commit instead of blanking it while it loads.
// Not a top-level await: the route chunk imports from this module, and would
// wait forever for it to finish evaluating.
if (rootElement.hasChildNodes()) {
  void router.load().then(render, render);
} else {
  render();
}
